import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import PDFExportButton from '@/components/PDFExportButton';
import LanguageSelector from '@/components/LanguageSelector';
import FocusAreasSelector from '@/components/FocusAreasSelector';
import ReadingProgressChart from '@/components/ReadingProgressChart';
import InfantToddlerSection from '@/components/InfantToddlerSection';
import ZazuLogo from '@/components/ZazuLogo';
import OnboardingTour, { useTour } from '@/components/OnboardingTour';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
// ImpersonationBanner is mounted globally in App.js — do not duplicate here.
import DailyTasksWidget from '@/components/DailyTasksWidget';
import StreakWidget from '@/components/StreakWidget';
import LeaderboardWidget from '@/components/LeaderboardWidget';
import AnalyticsWidget from '@/components/AnalyticsWidget';
import {
  Plus, Settings, LogOut, BookOpen, Trophy, Star,
  Clock, Play, Crown, User, ChevronRight, HelpCircle, GraduationCap, Key, Copy, BarChart3, RotateCcw, Building2, Search
} from 'lucide-react';

// Define which features are appropriate for each age group
const AGE_GROUP_FEATURES = {
  infant: {
    showReadingProgress: false,
    showBadges: false,
    showXP: false,
    showLevel: false,
    showAssessments: false,
    showFocusAreas: false,
    label: 'Infant (3-12 mo)'
  },
  toddler: {
    showReadingProgress: false,
    showBadges: true,
    showXP: true,
    showLevel: true,
    showAssessments: false,
    showFocusAreas: true,
    label: 'Toddler (1-3 yr)'
  },
  preschool: {
    showReadingProgress: true,
    showBadges: true,
    showXP: true,
    showLevel: true,
    showAssessments: true,
    showFocusAreas: true,
    label: 'Preschool (3-5 yr)'
  },
  early_elementary: {
    showReadingProgress: true,
    showBadges: true,
    showXP: true,
    showLevel: true,
    showAssessments: true,
    showFocusAreas: true,
    label: 'Early Elementary (5-7 yr)'
  },
  elementary: {
    showReadingProgress: true,
    showBadges: true,
    showXP: true,
    showLevel: true,
    showAssessments: true,
    showFocusAreas: true,
    label: 'Elementary (7-10 yr)'
  },
  middle: {
    showReadingProgress: true,
    showBadges: true,
    showXP: true,
    showLevel: true,
    showAssessments: true,
    showFocusAreas: true,
    label: 'Middle (10-12 yr)'
  }
};

const ParentDashboard = () => {
  const { user, logout, api } = useAuth();
  const { toggleTheme, isKidsMode } = useTheme();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const { resetTour } = useTour('parent');

  useEffect(() => {
    // Interns & employees can freely explore the parent side — they're comped Premium.
    // They can jump back to their workspace via the "My workspace" button in the header.
    fetchChildren();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
      toast.error('Failed to load children profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const generateStudentCode = async (childId) => {
    try {
      const response = await api.post(`/children/${childId}/generate-code`);
      toast.success(`Student code generated: ${response.data.student_code}`);
      fetchChildren(); // Refresh to show new code
    } catch (error) {
      toast.error('Failed to generate student code');
    }
  };

  const copyStudentCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Student code copied to clipboard!');
  };

  const getAgeGroupLabel = (ageGroup) => {
    return AGE_GROUP_FEATURES[ageGroup]?.label || ageGroup;
  };

  const getAgeGroupFeatures = (ageGroup) => {
    return AGE_GROUP_FEATURES[ageGroup] || AGE_GROUP_FEATURES.preschool;
  };

  const getLevelProgress = (level) => {
    return ((level % 5) / 5) * 100 || 100;
  };

  // Check if any child is infant or toddler
  const hasInfantOrToddler = children.some(c => ['infant', 'toddler'].includes(c.age_group));

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-slate-50">
      {/* Impersonation Banner */}
      {/* ImpersonationBanner is mounted globally in App.js */}
      
      {/* Email Verification Banner */}
      <EmailVerificationBanner />
      
      {/* Onboarding Tour for first-time users */}
      <OnboardingTour userType="parent" />
      
      {/* Header */}
      <header className="glass border-b border-white/40 sticky top-0 z-50 shadow-[0_1px_0_rgba(13,148,136,0.06)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-16 py-2 gap-2 flex-wrap sm:flex-nowrap">
            <Link to="/" data-testid="dashboard-logo-link" className="flex-shrink-0">
              <ZazuLogo size="md" />
            </Link>
            
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
              <LanguageSelector variant="ghost" />
              <Link to="/lessons/search" data-testid="parent-lesson-search-link">
                <Button variant="ghost" size="sm" title="Search & create lessons" className="gap-1">
                  <Search className="w-4 h-4" /> <span className="hidden sm:inline">Find lessons</span>
                </Button>
              </Link>
              <Link to="/help">
                <Button variant="ghost" size="icon" data-testid="help-btn" title="Help Center">
                  <HelpCircle className="w-5 h-5" />
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={resetTour}
                data-testid="restart-tour-btn"
                title="Restart Tour"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                title={isKidsMode ? "Switch to Parent Mode" : "Switch to Kids Mode"}
              >
                {isKidsMode ? <User className="w-5 h-5" /> : <Star className="w-5 h-5" />}
              </Button>
              <Link to="/settings" data-tour="settings-button">
                <Button variant="ghost" size="icon" data-testid="settings-btn">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              {user?.account_type === 'teacher' && (
                <Link to="/teacher">
                  <Button variant="outline" size="sm" data-testid="teacher-link">
                    <GraduationCap className="w-4 h-4 mr-1" /> Teacher
                  </Button>
                </Link>
              )}
              {(user?.account_type === 'org_admin' || user?.account_type === 'organization') && (
                <Link to="/organization/admin">
                  <Button variant="outline" size="sm" data-testid="org-admin-link" className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100">
                    <Building2 className="w-4 h-4 mr-1" /> Org Admin
                  </Button>
                </Link>
              )}
              {user?.is_admin ? (
                <Link to="/admin">
                  <Button variant="outline" size="sm" data-testid="admin-link">
                    <Crown className="w-4 h-4 mr-1" /> Admin
                  </Button>
                </Link>
              ) : (user?.is_staff || user?.intern_role) ? (
                <Link to="/intern/dashboard">
                  <Button variant="outline" size="sm" data-testid="workspace-link" className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100">
                    <Crown className="w-4 h-4 mr-1" /> My workspace
                  </Button>
                </Link>
              ) : null}
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 tracking-tight">
            Welcome back, <span className="bg-gradient-to-br from-teal-600 to-cyan-500 bg-clip-text text-transparent">{user?.name}</span>!
          </h1>
          <p className="text-slate-600">
            {user?.subscription_plan === 'free' ? (
              <span>You're on the Free plan. <Link to="/pricing" className="text-sky-500 hover:underline">Upgrade for more features</Link></span>
            ) : (
              <span>You're on the {user?.subscription_plan} plan</span>
            )}
          </p>
        </div>

        {/* Upgrade CTA — shown only for free users */}
        {user?.subscription_plan === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            data-testid="free-upgrade-banner"
          >
            <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 text-white overflow-hidden">
              <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-1">Unlock the full Zazu experience</h3>
                    <p className="text-white/90 text-sm max-w-xl">
                      Add up to 5 children, AI adaptive learning, unlimited activities, weekly progress reports, and the Bible curriculum. Cancel anytime.
                    </p>
                  </div>
                </div>
                <Link to="/pricing" className="flex-shrink-0">
                  <Button
                    size="lg"
                    className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-md"
                    data-testid="upgrade-to-paid-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" /> Upgrade — from $4.99/mo
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Children Section */}
        <div className="mb-8" data-tour="children-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-xl text-slate-900">Your Children</h2>
            <Link to="/child/new">
              <Button data-testid="add-child-btn" className="bg-teal-500 hover:bg-teal-600">
                <Plus className="w-5 h-5 mr-2" /> Add Child
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-16 w-16 bg-slate-200 rounded-full mb-4" />
                    <div className="h-6 w-32 bg-slate-200 rounded mb-2" />
                    <div className="h-4 w-24 bg-slate-200 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : children.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-teal-500" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-2">No children yet</h3>
                <p className="text-slate-600 mb-6">Add your first child to start their learning journey!</p>
                <Link to="/child/new">
                  <Button data-testid="add-first-child-btn" className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-5 h-5 mr-2" /> Add Your First Child
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child, index) => {
                const features = getAgeGroupFeatures(child.age_group);
                return (
                <motion.div
                  key={child.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`child-card-${child.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                            {child.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">{child.name}</h3>
                            <p className="text-sm text-slate-500">{getAgeGroupLabel(child.age_group)}</p>
                          </div>
                        </div>
                        <Link to={`/child/${child.id}/edit`}>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            Edit
                          </Button>
                        </Link>
                      </div>

                      {/* Level & XP - Only show for toddler+ */}
                      {features.showLevel && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-600">Level {child.current_level}</span>
                            {features.showXP && (
                              <span className="text-slate-500">{child.total_xp} XP</span>
                            )}
                          </div>
                          <Progress value={getLevelProgress(child.current_level)} className="h-2" />
                        </div>
                      )}

                      {/* Stats - Only show relevant ones */}
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        {features.showBadges && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Trophy className="w-4 h-4 text-yellow-500" />
                            <span>{child.badges?.length || 0} badges</span>
                          </div>
                        )}
                        {(child.has_disability || child.is_esl_learner) && (
                          <div className="flex items-center gap-1 text-slate-500">
                            <Star className="w-4 h-4 text-purple-500" />
                            <span>Special support</span>
                          </div>
                        )}
                      </div>

                      {/* Student Code */}
                      <div className="mb-4 p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Student Login Code:</span>
                          {child.student_code ? (
                            <button 
                              onClick={() => copyStudentCode(child.student_code)}
                              className="flex items-center gap-1 text-sm font-mono font-bold text-teal-600 hover:text-teal-700"
                              title="Click to copy"
                            >
                              {child.student_code}
                              <Copy className="w-3 h-3" />
                            </button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => generateStudentCode(child.id)}
                              className="h-6 text-xs"
                            >
                              <Key className="w-3 h-3 mr-1" /> Generate
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        <Link to={`/learn/${child.id}`} className="flex-1 min-w-[120px]" data-tour="learning-hub">
                          <Button 
                            className="w-full bg-teal-500 hover:bg-teal-600"
                            data-testid={`start-learning-${child.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" /> Start Learning
                          </Button>
                        </Link>
                        <Link to={`/explore/${child.id}`} data-testid={`assign-topic-${child.id}`}>
                          <Button variant="outline" size="icon" title="Assign a topic to learn">
                            <BookOpen className="w-4 h-4" />
                          </Button>
                        </Link>
                        {features.showFocusAreas && (
                          <FocusAreasSelector 
                            childId={child.id} 
                            childName={child.name}
                            onUpdate={fetchChildren}
                          />
                        )}
                        {features.showBadges && (
                          <Link to={`/badges/${child.id}`}>
                            <Button variant="outline" size="icon" data-testid={`badges-btn-${child.id}`}>
                              <Trophy className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                        {/* Progress Report Button */}
                        <Link to={`/progress-report/${child.id}`}>
                          <Button variant="outline" size="icon" data-testid={`progress-report-btn-${child.id}`} title="View Progress Report">
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        </Link>
                        <PDFExportButton childId={child.id} childName={child.name} />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )})}
            </div>
          )}
        </div>

        {/* Infant/Toddler Section - Show for children ages 0-3 */}
        {children.filter(c => ['infant', 'toddler'].includes(c.age_group)).map(child => (
          <div key={`infant-toddler-${child.id}`} className="mb-8">
            <InfantToddlerSection 
              childId={child.id}
              childName={child.name}
              ageGroup={child.age_group}
            />
          </div>
        ))}

        {/* Family Story Time Banner */}
        {children.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Link to="/family-story-time" data-testid="family-story-time-banner">
              <Card className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white hover:shadow-xl transition-all hover:scale-[1.01] cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl">
                        📖
                      </div>
                      <div>
                        <h3 className="font-bold text-xl">Family Story Time</h3>
                        <p className="text-pink-100">Schedule special Bible story moments with your little ones</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">⏰</span>
                      <ChevronRight className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        )}

        {/* Daily Tasks Section */}
        {children.length > 0 && (
          <div className="mb-8" data-tour="daily-tasks-section">
            <h2 className="font-bold text-xl text-slate-900 mb-4">Daily Learning Tasks</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {children.filter(c => !['infant'].includes(c.age_group)).map(child => (
                <div key={`daily-tasks-${child.id}`}>
                  <p className="text-sm text-slate-600 mb-2 font-medium">{child.name}'s Tasks</p>
                  <DailyTasksWidget 
                    childId={child.id} 
                    compact={children.length > 2}
                    onTaskComplete={() => fetchChildren()}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Streaks & Leaderboard Section */}
        {children.length > 0 && children.some(c => !['infant'].includes(c.age_group)) && (
          <div className="mb-8" data-tour="gamification-section">
            <h2 className="font-bold text-xl text-slate-900 mb-4">Streaks & Leaderboard</h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Streaks for each child */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-600">Daily Streaks</h3>
                {children.filter(c => !['infant'].includes(c.age_group)).map(child => (
                  <div key={`streak-${child.id}`}>
                    <p className="text-xs text-slate-500 mb-1">{child.name}</p>
                    <StreakWidget childId={child.id} compact={children.length > 1} />
                  </div>
                ))}
              </div>
              
              {/* Family Leaderboard */}
              <LeaderboardWidget 
                parentId={user?.id} 
                organizationId={user?.organization_id}
              />
            </div>
          </div>
        )}

        {/* Website Analytics - Show for all users */}
        <div className="mb-8">
          <AnalyticsWidget compact />
        </div>

        {/* Quick Stats - Only show if children have appropriate features */}
        {children.length > 0 && children.some(c => getAgeGroupFeatures(c.age_group).showXP) && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-tour="progress-section">
            <Link to="/children" data-testid="total-children-widget">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Children</p>
                      <p className="text-2xl font-bold text-slate-900">{children.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            {children.some(c => getAgeGroupFeatures(c.age_group).showBadges) && (
              <Link to={children[0] ? `/badges/${children[0].id}` : '#'} data-testid="total-badges-widget" data-tour="badges-section">
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Badges</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {children.reduce((sum, c) => sum + (c.badges?.length || 0), 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {children.some(c => getAgeGroupFeatures(c.age_group).showXP) && (
              <Link to={children[0] ? `/learn/${children[0].id}` : '#'} data-testid="total-xp-widget">
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <Star className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total XP</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {children.reduce((sum, c) => sum + (c.total_xp || 0), 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {children.some(c => getAgeGroupFeatures(c.age_group).showLevel) && (
              <Link to={children[0] ? `/progress/${children[0].id}` : '#'} data-testid="avg-level-widget">
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Avg Level</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {Math.round(children.filter(c => getAgeGroupFeatures(c.age_group).showLevel).reduce((sum, c) => sum + (c.current_level || 1), 0) / Math.max(1, children.filter(c => getAgeGroupFeatures(c.age_group).showLevel).length))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        )}

        {/* Reading Progress Charts - One per child that qualifies */}
        {children.filter(c => getAgeGroupFeatures(c.age_group).showReadingProgress).map(child => (
          <div key={`reading-${child.id}`} className="mb-6">
            <ReadingProgressChart 
              childId={child.id} 
              childName={child.name}
              ageGroup={child.age_group}
            />
          </div>
        ))}
      </main>
    </div>
  );
};

export default ParentDashboard;
